import React, { useContext, useEffect } from 'react'
import { Navigate, RouterProvider, createBrowserRouter } from 'react-router-dom'

import FormPreview from './components/create-form/FormPreview'
import Modal from './components/modals/Modal'
import ProtectedRoute from './components/protected-route/ProtectedRoute'
import { AuthContext } from './contexts/auth-context'
const HomePage = React.lazy(() => import('./pages/home/index'))
import BusinessLayout from './layouts/BusinessLayout'
import HomeLayout from './layouts/HomeLayout'
import BusinessDashboard from './pages/business-dashboard/BusinessDashboard'
import BusinessFormHomePage from './pages/business-form-home-page'
import BusinessProfile from './pages/business-profile/BusinessProfile'
import CreateCard from './pages/create-card/create-card'
import CreateForm from './pages/create-form'
import MyCards from './pages/my-cards'
import MyForms from './pages/my-forms'
import NotFound from './pages/not-found'
import PaymentCancelPage from './pages/payment-cancel-page'
import PaymentSuccessPage from './pages/payment-success-page'
import UserPage from './pages/user-page'
import { navigatetoHome } from './utils/footer-navigations'
import UserLayout from './layouts/UserLayout'
import UserDashboard from './pages/user-profile'
import UserCards from './pages/user-cards'
import UserHistory from './pages/user-history'
import UpdateUserProfile from './pages/user-profile/update-user-profile'
import BusinessCustomers from './pages/business-customers/business-customers'
import CardsListPage from './pages/user-cards/cards-list'
import BusinessReportsCustomerDemographics from './pages/business-reports/customer-demographics'
import BusinessReward from './pages/business-customers/business-reward'
import BusinessReportsCustomerActivity from './pages/business-reports/customer-activity'
import BusinessReportsLoyalCustomer from './pages/business-reports/customer-loyal'
import RewardHistory from './pages/reward-history'
import BusinessReportsPurchaseTrends from './pages/business-reports/customer-purchase'
import BusinessCustomerSendMessage from './pages/business-customers/business-customer-send-message'
import BusinessMessage from './pages/business-message'
import ContactUs from './components/contact-us/index'

const router = createBrowserRouter([
  // public + qr home
  {
    element: <HomeLayout />,
    errorElement: <NotFound />,
    children: [
      { path: '/', element: <HomePage /> },
      { path: '/register/:id', element: <BusinessFormHomePage /> },
      { path: '/contact-us', element: <ContactUs /> },
      // { path: "/checkout", element: <CheckoutForm /> },
    ],
  },

  // customer routes
  {
    element: <UserLayout />,
    errorElement: <NotFound />,
    children: [
      {
        path: '/user',
        element: (
          <ProtectedRoute allowedRoles={['user']}>
            <UserPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/user/profile',
        element: (
          <ProtectedRoute allowedRoles={['user']}>
            <UserDashboard />
          </ProtectedRoute>
        ),
      },
      {
        path: '/user/profile/edit',
        element: (
          <ProtectedRoute allowedRoles={['user']}>
            <UpdateUserProfile />
          </ProtectedRoute>
        ),
      },
      {
        path: '/user/cards',
        element: (
          <ProtectedRoute allowedRoles={['user']}>
            <UserCards />
          </ProtectedRoute>
        ),
      },
      {
        path: '/user/cards/:businessId',
        element: (
          <ProtectedRoute allowedRoles={['user']}>
            <CardsListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/user/rewards/:id',
        element: (
          <ProtectedRoute allowedRoles={['user']}>
            <RewardHistory />
          </ProtectedRoute>
        ),
      },
      {
        path: '/user/history',
        element: (
          <ProtectedRoute allowedRoles={['user']}>
            <UserHistory />
          </ProtectedRoute>
        ),
      },
    ],
  },

  // business routes
  {
    element: <BusinessLayout />,
    errorElement: <NotFound />,
    children: [
      {
        path: '/business',
        element: <Navigate to="/business/dashboard" replace />,
      },
      {
        path: '/business/dashboard',
        element: (
          <ProtectedRoute allowedRoles={['Business']}>
            <BusinessDashboard />
          </ProtectedRoute>
        ),
      },
      {
        path: '/business/profile',
        element: <Navigate to="/business/profile/my-profile" replace />,
      },
      {
        path: '/business/profile/:tab',
        element: (
          <ProtectedRoute allowedRoles={['Business']}>
            <BusinessProfile />
          </ProtectedRoute>
        ),
      },

      {
        path: '/business/create-form',
        element: (
          <ProtectedRoute allowedRoles={['Business']} allowedPlans={['premium', 'enterprise']}>
            <CreateForm />
          </ProtectedRoute>
        ),
      },

      {
        path: '/business/forms/:id',
        element: (
          <ProtectedRoute allowedRoles={['Business']}>
            <FormPreview />
          </ProtectedRoute>
        ),
      },

      {
        path: '/business/create-card',
        element: (
          <ProtectedRoute allowedRoles={['Business']}>
            <CreateCard />
          </ProtectedRoute>
        ),
      },

      {
        path: '/business/my-cards',
        element: (
          <ProtectedRoute allowedRoles={['Business']}>
            <MyCards />
          </ProtectedRoute>
        ),
      },

      {
        path: '/business/edit-card',
        element: (
          <ProtectedRoute allowedRoles={['Business']}>
            <CreateCard />
          </ProtectedRoute>
        ),
      },

      {
        path: '/business/my-forms',
        element: (
          <ProtectedRoute allowedRoles={['Business']}>
            <MyForms />
          </ProtectedRoute>
        ),
      },

      {
        path: '/business/my-customers',
        element: (
          <ProtectedRoute allowedRoles={['Business']}>
            <BusinessCustomers />
          </ProtectedRoute>
        ),
      },

      {
        path: '/business/my-customers/card/:id',
        element: (
          <ProtectedRoute allowedRoles={['Business']}>
            <BusinessReward />
          </ProtectedRoute>
        ),
      },

      // message -- route
      {
        path: '/business/message',
        element: (
          <ProtectedRoute allowedRoles={['Business']} allowedPlans={['premium', 'enterprise']}>
            <BusinessMessage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/business/my-customers/message/:id/:name/:email',
        element: (
          <ProtectedRoute allowedRoles={['Business']} allowedPlans={['premium', 'enterprise']}>
            <BusinessCustomerSendMessage />
          </ProtectedRoute>
        ),
      },

      {
        path: '/business/reports/customer/demographics',
        element: (
          <ProtectedRoute allowedRoles={['Business']}>
            <BusinessReportsCustomerDemographics />
          </ProtectedRoute>
        ),
      },

      {
        path: '/business/reports/customer/activity',
        element: (
          <ProtectedRoute allowedRoles={['Business']}>
            <BusinessReportsCustomerActivity />
          </ProtectedRoute>
        ),
      },

      {
        path: '/business/reports/customer/loyal',
        element: (
          <ProtectedRoute allowedRoles={['Business']}>
            <BusinessReportsLoyalCustomer />
          </ProtectedRoute>
        ),
      },

      {
        path: '/business/reports/customer/purchase',
        element: (
          <ProtectedRoute allowedRoles={['Business']}>
            <BusinessReportsPurchaseTrends />
          </ProtectedRoute>
        ),
      },

      // ---------------------------------- stripe route ------------------------------------ //
      // --> payment success route
      {
        path: '/business/payment/success',
        element: (
          <ProtectedRoute allowedRoles={['Business']}>
            <PaymentSuccessPage />
          </ProtectedRoute>
        ),
      },

      // --> payment cancel route
      {
        path: '/business/payment/cancel',
        element: (
          <ProtectedRoute allowedRoles={['Business']}>
            <PaymentCancelPage />
          </ProtectedRoute>
        ),
      },
    ],
  },

  // optional unauthorized page
  {
    path: '/unauthorized',
    element: (
      <div className="text-center mt-10 text-xl text-red-600 flex flex-col items-center">
        🔒 You are not authorized to access this page.
        <button
          className="mt-3 cursor-pointer px-4 py-1.5 bg-blue-500 text-white text-sm font-medium rounded-full 
                                               hover:bg-blue-600 transition-colors"
          onClick={(e) => {
            e.stopPropagation()
            window.location.href = '/'
          }}
        >
          Go Back
        </button>
      </div>
    ),
  },

  {
    path: '/purchase-plan',
    element: (
      <div className="text-center mt-10 text-xl text-red-600 flex flex-col items-center">
        🔒 You can not visit this page without an active plan.
        <button
          className="mt-3 cursor-pointer px-4 py-1.5 bg-blue-500 text-white text-sm font-medium rounded-full 
                                           hover:bg-blue-600 transition-colors"
          onClick={(e) => {
            e.stopPropagation()
            navigatetoHome('/#prices')
          }}
        >
          Purchase Plan
        </button>
      </div>
    ),
  },
])

function App() {
  const { auth, loading, isLoginModalOpen, modalContent } = useContext(AuthContext)

  useEffect(() => {
    // When auth loads and user is authenticated, check for a stored return URL
    if (!loading && auth.authenticate) {
      const returnUrl = sessionStorage.getItem('returnUrl')
      if (returnUrl) {
        // Clear the stored URL
        sessionStorage.removeItem('returnUrl')

        // Check if the return URL is appropriate for the user's role
        const userRole = auth.user?.role
        const isValidReturn =
          (userRole === 'user' && returnUrl.startsWith('/user')) ||
          (userRole === 'Business' && returnUrl.startsWith('/business'))

        // Navigate to the stored URL if valid
        if (isValidReturn) {
          window.history.replaceState({}, '', returnUrl)
        } else {
          // Navigate to the appropriate dashboard based on role
          const dashboardUrl = userRole === 'user' ? '/user' : '/business'
          window.history.replaceState({}, '', dashboardUrl)
        }
      }
    }
  }, [loading, auth])

  return (
    <>
      <Modal isOpen={isLoginModalOpen}>{modalContent}</Modal>
      <RouterProvider router={router} />
    </>
  )
}

export default App
